import { Box, Circle, Icon, Text } from '@chakra-ui/react';
import assert from 'assert';
import _ from 'lodash';
import React from 'react';
import { IconType } from 'react-icons';

import { date as commonDate } from '@finley-technologies/common';

import {
  BankAccountWithInstitutionAndDesignationGQLType,
  EventWithActorGQLType,
  FundingRequestEventForRenderFragmentGQLType,
  VerificationEventForRenderFragmentGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { fundingRequestKeyToVerb } from 'packages/common-frontend/constants';
import {
  bankAccountToDisplayString,
  dollarNumberWithCommas,
} from 'packages/common-frontend/utils';
import { FiCheck, FiDollarSign, FiPlay, FiSend, FiX } from 'react-icons/fi';

const fundingRequestEvent: {
  [key: string]: {
    generateCopy: (
      event: any,
      creditFacilityName: string,
      bankAccountsMap: Map<
        string,
        BankAccountWithInstitutionAndDesignationGQLType
      >,
    ) => string;
    icon: IconType;
  };
} = {
  requestPending: {
    generateCopy: (event: EventWithActorGQLType, creditFacilityName: string) =>
      `${event.actorName} initiated a funding request for ${creditFacilityName}`,
    icon: FiPlay,
  },
  requestUnsubmitted: {
    generateCopy: (_event: EventWithActorGQLType, creditFacilityName: string) =>
      `Funding request for ${creditFacilityName} was unsubmitted`,
    icon: FiX,
  },
  requestSubmitted: {
    generateCopy: (event: EventWithActorGQLType, creditFacilityName: string) =>
      `${event.actorName} submitted a funding request for ${creditFacilityName}`,
    icon: FiSend,
  },
  requestCanceled: {
    generateCopy: (event: EventWithActorGQLType, creditFacilityName: string) =>
      `${event.actorName} canceled a funding request for ${creditFacilityName}`,
    icon: FiX,
  },
  requestApproved: {
    generateCopy: (_event: EventWithActorGQLType, creditFacilityName: string) =>
      `${_event.actorName} approved a funding request for ${creditFacilityName}`,
    icon: FiCheck,
  },
  requestRejected: {
    generateCopy: (_event: EventWithActorGQLType, creditFacilityName: string) =>
      `Funding request for ${creditFacilityName} was rejected `,
    icon: FiX,
  },
  requestExpired: {
    generateCopy: (_event: EventWithActorGQLType, creditFacilityName: string) =>
      `Funding request for ${creditFacilityName} expired`,
    icon: FiX,
  },
  componentSettled: {
    generateCopy: (
      event: FundingRequestEventForRenderFragmentGQLType,
      _creditFacilityName: string,
      bankAccountsMap: Map<
        string,
        BankAccountWithInstitutionAndDesignationGQLType
      >,
    ) => {
      const { payload } = event;
      assert(
        payload.__typename === 'FundingRequestComponentPayload',
        `componentSettled event has an incorrect payload type of ${payload.__typename}`,
      );

      const {
        amount,
        destinationBankAccountId,
        type,
      } = payload.fundingRequestComponent;
      const destinationBankAccount = bankAccountsMap.get(
        destinationBankAccountId,
      );
      return `A ${dollarNumberWithCommas(amount)} ${
        fundingRequestKeyToVerb[type]
      } transfer to ${bankAccountToDisplayString(
        destinationBankAccount,
      )} was completed`;
    },
    icon: FiDollarSign,
  },
};

const verificationReportEvent: {
  [key: string]: {
    generateCopy: (event: any, creditFacilityName: string) => string;
    icon: IconType;
  };
} = {
  verificationReportApproved: {
    generateCopy: (event: EventWithActorGQLType, creditFacilityName: string) =>
      `${event.actorName} approved a verification report for ${creditFacilityName}`,
    icon: FiCheck,
  },
  verificationReportRejected: {
    generateCopy: (event: EventWithActorGQLType, creditFacilityName: string) =>
      `${event.actorName} rejected a verification report for ${creditFacilityName}`,
    icon: FiX,
  },
};

export const FundingRequestEventHistory = ({
  events,
  creditFacilityName,
  bankAccountsMap,
}: {
  events: FundingRequestEventForRenderFragmentGQLType[];
  creditFacilityName: string;
  bankAccountsMap: Map<string, BankAccountWithInstitutionAndDesignationGQLType>;
}) => {
  return (
    <Box>
      {_.map(events, (event) => {
        const { actionKey, created_at: createdAt } = event;
        assert(
          _.isString(actionKey) && actionKey in fundingRequestEvent,
          `${actionKey} is an invalid action key for funding request event`,
        );
        return (
          <Box
            alignContent="center"
            display="flex"
            key={`funding-request-event-${event.id}`}
          >
            <Circle size="40px" bg="millet.50">
              <Icon as={fundingRequestEvent[actionKey].icon} />
            </Circle>
            <Box padding="5px 10px">
              <Text>
                {fundingRequestEvent[actionKey].generateCopy(
                  event,
                  creditFacilityName,
                  bankAccountsMap,
                )}
              </Text>
              <Text fontSize="sm" color="gray" mb="10px">
                {commonDate.epochToLocaleDateString(createdAt)}
              </Text>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export const VerificationReportEventHistory = ({
  events,
  creditFacilityName,
}: {
  events: VerificationEventForRenderFragmentGQLType[];
  creditFacilityName: string;
}) => {
  return (
    <Box>
      {_.map(events, (event) => {
        const { actionKey, created_at: createdAt } = event;
        assert(
          _.isString(actionKey) && actionKey in verificationReportEvent,
          `${actionKey} is an invalid action key for verification report event`,
        );
        return (
          <Box
            alignContent="center"
            display="flex"
            key={`verification-report-event-${event.id}`}
          >
            <Circle size="40px" bg="millet.50">
              <Icon as={verificationReportEvent[actionKey].icon} />
            </Circle>
            <Box padding="5px 10px">
              <Text>
                {verificationReportEvent[actionKey].generateCopy(
                  event,
                  creditFacilityName,
                )}
              </Text>
              <Text fontSize="sm" color="gray" mb="10px">
                {commonDate.epochToLocaleDateString(createdAt)}
              </Text>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
